import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "@CubeComponents/constants";
import axiosWrapper from "@AxiosWrapper";
import { QueryBuilder, executeQuery } from "../services/queryBuilder";
import { includedProjectsUrl } from "../services/dataManager";
import { setProjectData, setSpaceData } from "../reducers/breadcrumbsdata";
import { getCurrencyData } from "./Currency";
import type { GetSpaceByIdRequest, GetSpaceByIdResponse, SpaceDataItem } from "@ServerTypes/spaces/space.schema";
import type {
  GetIncludedProjectsControllerRequest,
  GetProjectByIdRequest,
  ProjectDataItem,
} from "@ServerTypes/projects/project.schema";
import type { Exact } from "@Helpers/types/exact";
import { toObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * `id` is the project's Mongo _id, exactly as `/getProjectById/:id` names it — but the fetch below goes to
 * `getIncludedProjectsController` (a `_id equal` filter), not to `/getProjectById`, so `originSource` is
 * THAT route's scope selector and is typed from its schema. `/getProjectById` itself never read an
 * `originSource` and no longer declares one.
 */
type CurrProjectDataRequest = GetProjectByIdRequest & {
  originSource?: NonNullable<GetIncludedProjectsControllerRequest["value"]>["originSource"];
};

export function getCurrProjectData<T extends Exact<CurrProjectDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const query = new QueryBuilder().where("_id", "equal", data.id);
    if (data.originSource) {
      query.addParams("originSource", data.originSource);
    }
    const queryResult = await executeQuery<Partial<ProjectDataItem> & { image?: string }>(includedProjectsUrl, query);
    const response = (queryResult.status ? queryResult.result[0] : undefined) ?? {};

    if (response.image) {
      dispatch(
        setProjectData({
          ...response,
          projectImageUrl: getUrlForImage({ id: data.id }, "projectImage"),
        }),
      );
    } else {
      dispatch(setProjectData({ ...response }));
    }
    // data.id is already validated (Exact<GetProjectByIdRequest>), but the Exact<> generic widens it
    // to a union here; toObjectIdString re-tags it without re-validating (runtime no-op, as String() was).
    // Fire-and-forget thunk: the currency lands in the store on its own schedule.
    void dispatch(getCurrencyData({ projectId: toObjectIdString(data.id) }));
  };
}

export function getCurrSpaceData<T extends Exact<GetSpaceByIdRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: a thunk body cannot await. The dead `.catch` that used to sit at the end of
    // this chain only rebuilt and dropped the rejection; leaving it off keeps the real one
    // unhandled, which is exactly what Sentry's global handler reports.
    void axiosWrapper.post<GetSpaceByIdResponse>(`${getBackendUrl()}/spaces/getSpaceById`, data).then((res) => {
      // The not-found / no-permission branches answer without `data`; the slice holds the space
      // shape itself, so an empty one is stored rather than a marker field it cannot carry.
      const spaceData: SpaceDataItem = res.data.status ? res.data.data : {};
      dispatch(setSpaceData(spaceData));
    });
  };
}
